import React from 'react'
import { calculateTier, TIERS } from '../lib/loyalty'

// ── LCA Volume Discount Tracker ───────────────────────────────
// Refined, on-brand: navy + gold + cream only.
// No medal emojis, no rainbow gradients — clean banking aesthetic.

export default function LoyaltyTierTracker({ jobs }) {
  const tier = calculateTier(jobs || [])
  const t    = tier.effective

  return (
    <div style={{
      background:    '#fff',
      border:        '1px solid #E8E6DF',
      borderRadius:  14,
      overflow:      'hidden',
      marginBottom:  16,
    }}>
      {/* ── HEADER — small gold accent bar at top ── */}
      <div style={{ height:3, background:'linear-gradient(90deg,#C9A84C,#9A7A30)' }} />

      {/* ── MAIN ROW — tier name, count, discount ── */}
      <div style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        padding:        '14px 18px',
        gap:            16,
        flexWrap:       'wrap',
      }}>
        <div>
          <div style={{
            fontSize:      9,
            color:         '#B8B5AB',
            letterSpacing: '0.12em',
            fontWeight:    600,
            marginBottom:  2,
          }}>VOLUME DISCOUNT TIER</div>
          <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
            <div style={{
              fontFamily:    'Cormorant Garamond,serif',
              fontSize:      22,
              fontWeight:    600,
              color:         '#0D1B3E',
              letterSpacing: '0.02em',
            }}>{t.label}</div>
            {t.discount > 0 && (
              <div style={{
                fontSize:      11,
                color:         '#9A7A30',
                fontWeight:    600,
                letterSpacing: '0.04em',
              }}>· {t.discount}% discount</div>
            )}
          </div>
        </div>

        <div style={{ textAlign:'right' }}>
          <div style={{
            fontSize:      9,
            color:         '#B8B5AB',
            letterSpacing: '0.1em',
            fontWeight:    600,
          }}>LAST 30 DAYS</div>
          <div style={{
            fontFamily: 'Cormorant Garamond,serif',
            fontSize:   20,
            fontWeight: 700,
            color:      '#0D1B3E',
            lineHeight: 1.1,
          }}>{tier.currentCount}<span style={{ fontSize:11, fontWeight:400, color:'#7A7870', marginLeft:4 }}>order{tier.currentCount !== 1 ? 's' : ''}</span></div>
        </div>
      </div>

      {/* ── GRACE PERIOD — only when active, very subtle ── */}
      {tier.isGrace && (
        <div style={{
          padding:    '6px 18px',
          background: '#FAFAF8',
          borderTop:  '1px solid #F0EEE8',
          fontSize:   10,
          color:      '#7A7870',
          letterSpacing: '0.02em',
        }}>
          <span style={{ color:'#9A7A30', fontWeight:600 }}>Tier hold:</span> {t.label} benefits maintained for {tier.graceDaysRemaining} more day{tier.graceDaysRemaining !== 1 ? 's' : ''}.
        </div>
      )}

      {/* ── PROGRESS — only if there's a next tier ── */}
      {tier.nextTier ? (
        <div style={{
          padding:    '12px 18px 14px',
          background: '#FAFAF8',
          borderTop:  '1px solid #F0EEE8',
        }}>
          <div style={{
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'baseline',
            marginBottom:   8,
            fontSize:       11,
            color:          '#7A7870',
          }}>
            <span>
              {tier.ordersToNext === 0
                ? <>You've reached the threshold for <span style={{ color:'#0D1B3E', fontWeight:600 }}>{tier.nextTier.label}</span>.</>
                : <>
                    <span style={{ color:'#0D1B3E', fontWeight:600 }}>{tier.ordersToNext}</span> more order{tier.ordersToNext !== 1 ? 's' : ''} to <span style={{ color:'#0D1B3E', fontWeight:600 }}>{tier.nextTier.label}</span>
                    {tier.nextTier.discount > 0 && <span style={{ color:'#9A7A30' }}> · {tier.nextTier.discount}% discount</span>}
                  </>
              }
            </span>
            <span style={{ fontSize:10, color:'#B8B5AB', fontWeight:500, letterSpacing:'0.04em' }}>{tier.progressPct}%</span>
          </div>

          {/* Slim, refined progress bar */}
          <div style={{
            position:     'relative',
            height:       4,
            background:   '#E8E6DF',
            borderRadius: 2,
            overflow:     'hidden',
          }}>
            <div style={{
              position:     'absolute',
              top:          0,
              left:         0,
              height:       '100%',
              width:        `${tier.progressPct}%`,
              background:   'linear-gradient(90deg,#9A7A30,#C9A84C)',
              borderRadius: 2,
              transition:   'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }} />
          </div>

          {/* TIER MARKERS — minimal text-only ladder */}
          <div style={{
            display:        'flex',
            justifyContent: 'space-between',
            marginTop:      10,
            fontSize:       9,
            color:          '#B8B5AB',
            letterSpacing:  '0.08em',
            fontWeight:     500,
          }}>
            {TIERS.map(tierItem => {
              const isCurrent = tierItem.key === t.key
              return (
                <div key={tierItem.key} style={{
                  textAlign: 'center',
                  flex:      1,
                  color:     isCurrent ? '#0D1B3E' : '#B8B5AB',
                  fontWeight: isCurrent ? 700 : 500,
                }}>
                  <div>{tierItem.label.toUpperCase()}</div>
                  <div style={{ fontSize:8, marginTop:2, color:isCurrent ? '#9A7A30' : '#C9C5BA', fontWeight:500 }}>
                    {tierItem.max === Infinity ? `${tierItem.min}+` : `${tierItem.min}–${tierItem.max}`}
                    {tierItem.discount > 0 && ` · ${tierItem.discount}%`}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        // PLATINUM — top tier, refined acknowledgment
        <div style={{
          padding:    '12px 18px',
          background: '#FAFAF8',
          borderTop:  '1px solid #F0EEE8',
          display:    'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap:        12,
          flexWrap:   'wrap',
        }}>
          <div style={{ fontSize:11, color:'#7A7870' }}>
            Top tier reached. Your dedicated manager: <span style={{ color:'#0D1B3E', fontWeight:600 }}>Nancy Phillips</span>.
          </div>
          <a href="mailto:nancy@loanclosingagent.com" style={{
            fontSize:       10,
            fontWeight:     600,
            color:          '#9A7A30',
            textDecoration: 'none',
            letterSpacing:  '0.06em',
            borderBottom:   '1px solid #C9A84C',
            paddingBottom:  1,
          }}>CONTACT DIRECTLY →</a>
        </div>
      )}
    </div>
  )
}
